import { Dispatch, ReactNode, SetStateAction } from "react";
import {
  MRT_EditActionButtons as MRTEditActionButtons,
  MRT_Row as MRTRow,
  MRT_TableInstance as MRTTableInstance
} from "material-react-table";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { CalendarMonth, MenuBook, PersonOff } from "@mui/icons-material";
import { t } from "i18next";
import { DateTime } from "luxon";
import { FleetManagementRow } from "./types/FleetManagement-row.types";
import { FleetAttachment, FleetContractDetails, FleetStatus } from "./types/FleetManagement.types";
import { FleeStatusSelect, StatusDraftSetters } from "./FleetManagement-StatusSelect.partial";
import { OccupiedStatus } from "../FacilityManager/Domain/Types/Status";
import { ContractDetailsSection } from "./FleetManagement-ContractDetails.partial";
import { AttachmentsSection } from "./FleetManagement-Attachments.partial";
import { TransitionForSlideoutPanel } from "../SlideoutPanel/SlideoutPanelTransition.component";

export const editStatusSelect = (params: FleetManagementRow, draftSetters?: StatusDraftSetters) => {
  return <FleeStatusSelect params={params} draftSetters={draftSetters} />;
};

export const renderFuelCell = (params: FleetManagementRow) => <>{t(params.fuel)}</>;

export const renderStatusCell = (params: FleetManagementRow) => {
  if (params.status === FleetStatus.Blocked && params.blockedReason) {
    return (
      <>
        {t(params.status)}
        <br />
        <small style={{ color: "rgba(0, 0, 0, 0.6)" }}>{params.blockedReason}</small>
      </>
    );
  }
  return <>{t(params.status)}</>;
};

export type OdometerCellProps = {
  value: number | null;
  language: string;
};

export const renderOdometerCell = ({ value, language }: Readonly<OdometerCellProps>) => {
  if (value == null) return null;

  const locale = language === "de" ? "de-DE" : "en-US";
  const formattedOdometer = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
    value
  );

  return <>{formattedOdometer}</>;
};

export function FleetSlidePanelCreateEditService({
  table,
  row,
  internalEditComponents,
  headline
}: Readonly<{
  table: MRTTableInstance<FleetManagementRow>;
  row: MRTRow<FleetManagementRow>;
  internalEditComponents: ReactNode[];
  headline: string;
}>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const createOrEditDialogStyle = {
    height: "calc(-64px + 100vh)",
    maxHeight: "calc(-64px + 100vh)",
    maxWidth: isMobile ? "inherit" : "50vw",
    position: "absolute",
    right: 0,
    margin: 0,
    width: isMobile ? "100%" : "40vw",
    padding: 3,
    top: "64px",
    overflowY: "inherit"
  };

  return (
    <Dialog
      open
      data-testid="fleet-mgmt-edit-row-dialog"
      slotProps={{ paper: { sx: { ...createOrEditDialogStyle } } }}
      transitionDuration={400}
      slots={{ transition: TransitionForSlideoutPanel }}
    >
      {/* edit catering dialog contents */}
      <DialogTitle variant="h4">{headline}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1.5rem", mt: 2 }}>
        <Grid2 container data-testid="edit-fleet-dialog-contents" columnGap={4}>
          <Grid2 container size={{ xs: 12 }} rowGap={3.5}>
            {/* image */}
            {internalEditComponents[1]}
            {/* name */}
            {internalEditComponents[2]}
            {/* description */}
            {internalEditComponents[3]}
            {/* licensePlate */}
            {internalEditComponents[4]}
            {/* brandModel */}
            {internalEditComponents[5]}
            {/* fuel */}
            {internalEditComponents[7]}
            {/* numSeats */}
            {internalEditComponents[9]}
            {/* odometer */}
            {internalEditComponents[10]}
            {/* vehicleType */}
            {internalEditComponents[6]}
            {/* reach */}
            {internalEditComponents[8]}

            {/* company (assignment) */}
            {internalEditComponents[14]}

            {/* price per hour */}
            {internalEditComponents[15]}

            {/* location (assignment) */}
            {internalEditComponents[12]}

            {/* status - not editable - internalEditComponents[13]*/}

            {/* accessories/properties */}
            {internalEditComponents[11]}

            {/* buffer time */}
            {internalEditComponents[16]}

            {/* adminNotes (if isEditable) */}
            {internalEditComponents[17]}
            {/* userNotes (if isEditable) */}
            {internalEditComponents[18]}

            {/* fleetPermissions */}
            {internalEditComponents[19]}

            {/* attachments */}
            {internalEditComponents[22]}
          </Grid2>
        </Grid2>
      </DialogContent>

      <Divider sx={{ mt: 3, mb: 3 }} />

      <DialogActions>
        <MRTEditActionButtons variant="text" table={table} row={row} />
      </DialogActions>
    </Dialog>
  );
}

const hasRelevantBooking = (
  items: OccupiedStatus[] | undefined = [],
  timezone?: string
): boolean => {
  const now = timezone ? DateTime.now().setZone(timezone) : DateTime.now();

  return items.some(item => {
    const start = DateTime.fromISO(item.startTime, { zone: timezone });
    const end = DateTime.fromISO(item.endTime, { zone: timezone });
    return start.hasSame(now, "day") || (start <= now && end >= now);
  });
};

export const renderOccupiedBookingCell = ({
  params,
  isEditable,
  timezone,
  setSelectRow
}: {
  params: FleetManagementRow;
  isEditable: boolean;
  timezone: string;
  setSelectRow: (r: FleetManagementRow | null) => void;
}) => {
  // compare time
  const hasTodayBooking = hasRelevantBooking(params.occupiedBooking, timezone);
  const hasTodayBookingColor = hasTodayBooking ? "primary" : "default";

  // when it's editable check hasTodayBooking, when it's not editable, stay default color
  const color = isEditable ? hasTodayBookingColor : "default";

  const tooltipText = isEditable ? t("Manage events for this vehicle") : t("Show bookings");

  return (
    <Tooltip title={<Typography>{tooltipText}</Typography>}>
      <IconButton
        onClick={() => setSelectRow(params)}
        data-testid="fleet-booking-show-occupied-btn"
        color={color}
      >
        <CalendarMonth />
      </IconButton>
    </Tooltip>
  );
};

export const renderBlockedUsersCell = (
  params: FleetManagementRow,
  onOpen: (vehicleId: number, vehicleName: string) => void
) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", pr: 3 }}>
    <IconButton
      onClick={() => onOpen(params.id, params.name)}
      data-testid="fleet-blocked-users-btn"
    >
      <PersonOff />
    </IconButton>
  </Box>
);

export const renderLogbookCell = (
  params: FleetManagementRow,
  onOpen: (vehicleId: number, vehicleName: string, licensePlate?: string) => void
) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", pr: 3 }}>
    <IconButton
      onClick={() => onOpen(params.id, params.name, params.licensePlate)}
      data-testid="fleet-logbook-btn"
    >
      <MenuBook />
    </IconButton>
  </Box>
);

export const editContractDetails = (
  params: FleetManagementRow,
  contractDetailsDraft: FleetContractDetails,
  setContractDetailsDraft: Dispatch<SetStateAction<FleetContractDetails>>
) => {
  return (
    <ContractDetailsSection
      params={params}
      contractDetails={contractDetailsDraft}
      onChange={setContractDetailsDraft}
    />
  );
};

export const editAttachments = (
  params: FleetManagementRow,
  attachmentsDraft: FleetAttachment[],
  setAttachmentsDraft: Dispatch<SetStateAction<FleetAttachment[]>>,
  onDeleteExisting?: (vehicleId: number, attachmentId: number) => void | Promise<void>
) => {
  return (
    <AttachmentsSection
      attachments={attachmentsDraft}
      setAttachments={setAttachmentsDraft}
      vehicleId={params.id}
      onDeleteExisting={onDeleteExisting}
    />
  );
};
